// Simplified Place ID lookup for individual areas/localities
export class PlaceIdLookup {
  private placesService: any = null;

  constructor() {
    this.initializePlacesService();
  }

  private initializePlacesService() {
    if (typeof window !== 'undefined' && (window as any).google?.maps?.places) {
      // Create a hidden div for the places service
      const div = document.createElement('div');
      const map = new (window as any).google.maps.Map(div, {
        center: { lat: 19.0760, lng: 72.8777 }, // Mumbai coordinates
        zoom: 10
      });
      this.placesService = new (window as any).google.maps.places.PlacesService(map);
    }
  }

  /**
   * Search for a place using Places Service
   */
  private async searchPlace(query: string): Promise<string | null> {
    return new Promise((resolve) => {
      if (!this.placesService) {
        this.initializePlacesService();
        if (!this.placesService) {
          console.warn('Places service not available for:', query);
          resolve(null);
          return;
        }
      }

      const request = {
        query: query,
        fields: ['place_id', 'name', 'types'],
        locationBias: {
          center: { lat: 19.0760, lng: 72.8777 }, // Mumbai
          radius: 50000 // 50km radius
        }
      };

      this.placesService.findPlaceFromQuery(request, (results: any[], status: any) => {
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          const result = results[0];
          
          // Prefer administrative/locality types
          const isAppropriate = result.types?.some((type: string) => 
            type.includes('administrative_area') || 
            type.includes('sublocality') ||
            type.includes('political') ||
            type.includes('locality') ||
            type.includes('neighborhood') ||
            type.includes('route')
          );
          
          if (isAppropriate || !result.types || result.types.length === 0) {
            console.log(`✅ Found Place ID for "${query}": ${result.place_id}`);
            resolve(result.place_id);
          } else {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      });
    });
  }

  /**
   * Get Place ID for an area
   */
  async getAreaPlaceId(areaName: string, cityName?: string): Promise<string | null> {
    const searchQueries = [
      // Try with city context
      cityName ? `${areaName}, ${cityName}` : null,
      // Try with Mumbai context
      `${areaName} Mumbai`,
      // Try exact name
      areaName,
      // Try with area suffix
      `${areaName} area Mumbai`
    ].filter(Boolean) as string[];

    for (const query of searchQueries) {
      try {
        const placeId = await this.searchPlace(query);
        if (placeId) {
          return placeId;
        }
      } catch (error) {
        console.warn(`Error searching for area: ${query}`, error);
      }
      
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    console.warn(`❌ No Place ID found for area: ${areaName}`);
    return null;
  }

  /**
   * Get Place ID for a locality
   */
  async getLocalityPlaceId(localityName: string, areaName?: string, cityName?: string): Promise<string | null> {
    const searchQueries = [
      // Try with full context
      cityName && areaName ? `${localityName}, ${areaName}, ${cityName}` : null,
      // Try with city context
      cityName ? `${localityName}, ${cityName}` : null,
      // Try with Mumbai context
      `${localityName} Mumbai`,
      // Try exact name
      localityName,
      // Try with area context
      areaName ? `${localityName}, ${areaName} Mumbai` : null
    ].filter(Boolean) as string[];

    for (const query of searchQueries) {
      try {
        const placeId = await this.searchPlace(query);
        if (placeId) {
          return placeId;
        }
      } catch (error) {
        console.warn(`Error searching for locality: ${query}`, error);
      }
      
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    console.warn(`❌ No Place ID found for locality: ${localityName}`);
    return null;
  }

  /**
   * Get Place ID for a neighborhood (political)
   * Alias to locality lookup but kept explicit for clarity.
   */
  async getNeighborhoodPlaceId(neighborhoodName: string, areaName?: string, cityName?: string): Promise<string | null> {
    return this.getLocalityPlaceId(neighborhoodName, areaName, cityName);
  }

  /**
   * Get Place ID for a route (street)
   */
  async getRoutePlaceId(routeName: string, cityName?: string): Promise<string | null> {
    const searchQueries = [
      cityName ? `${routeName}, ${cityName}` : null,
      `${routeName} Mumbai`,
      routeName,
      `${routeName} road Mumbai`
    ].filter(Boolean) as string[];

    for (const query of searchQueries) {
      try {
        const placeId = await this.searchPlace(query);
        if (placeId) return placeId;
      } catch (e) {
        console.warn(`Error searching for route: ${query}`, e);
      }
      await new Promise(r => setTimeout(r, 200));
    }
    console.warn(`❌ No Place ID found for route: ${routeName}`);
    return null;
  }

  /**
   * Check if Google Maps is available
   */
  isAvailable(): boolean {
    return !!(window as any).google?.maps?.places;
  }
}

// Export singleton instance
export const placeIdLookup = new PlaceIdLookup();